import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Header from '../layout/Header';

const PROJETS_ALLOWED_EMAILS = [
  'dany.bourassa@bzinc.ca',
  'eric.hains@bzinc.ca',
  'philippe.bolduc@bzinc.ca',
  'jean-michel.caouette@bzinc.ca',
  'jean-pierre.leclerc@bzinc.ca',
  'francis.marquis@bzinc.ca',
  'pierre-luc.fortin@bzinc.ca',
  'vincent.bourassa@bzinc.ca',
  'stephane.paradis@bzinc.ca',
  'mikael.drouin-bouchard@bzinc.ca',
  'eric.durand@bzinc.ca',
];
const PROJETS_ADMIN_EMAILS = [
  'dany.bourassa@bzinc.ca',
  'eric.hains@bzinc.ca',
  'philippe.bolduc@bzinc.ca',
  'jean-michel.caouette@bzinc.ca',
  'jean-pierre.leclerc@bzinc.ca',
  'francis.marquis@bzinc.ca',
  'pierre-luc.fortin@bzinc.ca',
  'vincent.bourassa@bzinc.ca',
  'stephane.paradis@bzinc.ca',
  'eric.durand@bzinc.ca',
];

/** Mappe l'utilisateur PRIsME (parties prenantes) vers le format attendu par TactiCE */
function mapPrismeUserToProjet(user: { id?: number; email?: string; name?: string; role?: string } | null) {
  if (!user) return null;
  const emailLower = (user.email ?? '').toLowerCase();
  const isAdmin = PROJETS_ADMIN_EMAILS.includes(emailLower);
  return {
    id: user.id ?? 0,
    email: user.email ?? '',
    nom_client: user.name ?? 'Utilisateur PRIsME',
    type_client: isAdmin ? 'Administrateur' : (user.role ?? 'Utilisateur'),
  };
}

function HelloWorldBanner() {
  return (
    <div className="w-full border-b border-blue-100 bg-white px-4 py-3 text-center text-sm font-semibold text-blue-700 shadow-sm">
      Hello world
    </div>
  );
}

export default function Projets() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState<'projets' | 'taches'>('projets');
  const [ProjetContent, setProjetContent] = useState<React.ComponentType<any> | null>(null);
  type ProjetUser = { id: number; email: string; nom_client: string; type_client: string } | null;
  const [UserSimulationProvider, setUserSimulationProvider] = useState<React.ComponentType<{ children: React.ReactNode; initialUser?: ProjetUser }> | null>(null);

  const canAccess = !!user?.email && PROJETS_ALLOWED_EMAILS.includes(user.email.toLowerCase());
  const currentUserProjet = mapPrismeUserToProjet(user ? { id: (user as any).id, email: user.email, name: user.name, role: (user as any).role } : null);

  useEffect(() => {
    if (!canAccess) {
      navigate('/', { replace: true });
      return;
    }
    (window as any).__PROJET_API_BASE__ = '/api/projet';
    (window as any).__PROJET_CLIENTS_API_BASE__ = '/api';
    (window as any).__PROJET_RACI_CONTACTS_API_BASE__ = '/api';
    return () => {
      delete (window as any).__PROJET_API_BASE__;
      delete (window as any).__PROJET_CLIENTS_API_BASE__;
      delete (window as any).__PROJET_RACI_CONTACTS_API_BASE__;
    };
  }, [canAccess, navigate]);

  useEffect(() => {
    if (!canAccess) return;
    // @ts-expect-error - module .jsx du projet TactiCE (non typé)
    import('../../projet/src/contexts/UserSimulationContext.jsx').then((m) => setUserSimulationProvider(() => m.UserSimulationProvider));
  }, [canAccess]);

  useEffect(() => {
    if (!canAccess) return;
    const load = async () => {
      try {
        if (currentPage === 'projets') {
          // @ts-expect-error - module .jsx du projet TactiCE (non typé)
          const { default: ProjectsPage } = await import('../../projet/src/components/ProjectsPage.jsx');
          setProjetContent(() => ProjectsPage);
        } else {
          // @ts-expect-error - module .jsx du projet TactiCE (non typé)
          const { default: TasksCompactPage } = await import('../../projet/src/components/TasksCompactPage.jsx');
          setProjetContent(() => TasksCompactPage);
        }
      } catch (e) {
        console.error('Chargement section Projets:', e);
        setProjetContent(null);
      }
    };
    load();
  }, [canAccess, currentPage]);

  const onLogout = useCallback(() => {}, []);

  if (!canAccess) return null;

  if (!ProjetContent || !UserSimulationProvider) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-bz-blue" />
      </div>
    );
  }

  const Page = ProjetContent;
  const Provider = UserSimulationProvider;
  return (
    <Provider initialUser={currentUserProjet}>
      <div className="min-h-full bg-slate-50">
        <HelloWorldBanner />
        <Header
          title="Projets"
          subtitle="Portfolio de projets et tâches — suivi et livrables"
        />
        <Page
          setCurrentPage={setCurrentPage}
          currentUser={currentUserProjet}
          onLogout={onLogout}
          hideUserInHeader
        />
      </div>
    </Provider>
  );
}
